import { mui } from "@/mui";
import { Mail, SortDesc, Hash, Layers, Filter, Paperclip, CircleDot, Image } from "lucide-react";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

// Email display settings stored in localStorage
export interface EmailSettings {
  sortOrder: "newest" | "oldest";
  messagesPerAccount: number;
  displayMode: "aggregated" | "byAccount";
  attachmentRouting: "inline" | "fileTransfer";
  loadImages: boolean;
  whitelist: string[];
  blacklist: string[];
}

const DEFAULT_SETTINGS: EmailSettings = {
  sortOrder: "newest",
  messagesPerAccount: 50,
  displayMode: "aggregated",
  attachmentRouting: "fileTransfer",
  loadImages: false,
  whitelist: [],
  blacklist: [],
};

const MESSAGES_PER_ACCOUNT_OPTIONS = [25, 50, 100, 200];

export function getEmailSettings(): EmailSettings {
  try {
    const stored = localStorage.getItem("moby_pref_emailSettings");
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return DEFAULT_SETTINGS;
}

export function setEmailSettings(settings: EmailSettings): void {
  try {
    localStorage.setItem("moby_pref_emailSettings", JSON.stringify(settings));
    // Dispatch event so email list can react
    window.dispatchEvent(new CustomEvent("email-settings-updated", { detail: settings }));
  } catch (error) {
    console.error("Failed to save email settings:", error);
  }
}

export function EmailSettingsDialog({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  
  // Local state
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [messagesPerAccount, setMessagesPerAccount] = useState(50);
  const [displayMode, setDisplayMode] = useState<"aggregated" | "byAccount">("aggregated");
  const [attachmentRouting, setAttachmentRouting] = useState<"inline" | "fileTransfer">("fileTransfer");
  const [loadImages, setLoadImages] = useState(false);
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [blacklist, setBlacklist] = useState<string[]>([]);

  // Load settings when dialog opens
  useEffect(() => {
    if (open) {
      const settings = getEmailSettings();
      setSortOrder(settings.sortOrder);
      setMessagesPerAccount(settings.messagesPerAccount);
      setDisplayMode(settings.displayMode);
      setAttachmentRouting(settings.attachmentRouting);
      setLoadImages(settings.loadImages);
      setWhitelist(settings.whitelist);
      setBlacklist(settings.blacklist);
    }
  }, [open]);

  const handleSave = () => {
    const settings: EmailSettings = {
      sortOrder,
      messagesPerAccount,
      displayMode,
      attachmentRouting,
      loadImages,
      whitelist,
      blacklist,
    };
    
    setEmailSettings(settings);
    toast.success("Email settings saved!");
    setOpen(false);
  };

  return (
    <mui.Dialog.Root open={open} onOpenChange={setOpen}>
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop />
        <mui.Dialog.Popup className="max-w-md max-h-[90vh] flex flex-col">
          <mui.Dialog.Title className="flex items-center gap-2">
            <Mail size={20} />
            Email Settings
          </mui.Dialog.Title>
          <mui.Dialog.Description>
            Configure how emails are displayed and processed
          </mui.Dialog.Description>

          <div className="space-y-6 mt-6 overflow-y-auto flex-1 px-1">
            {/* Sort Order */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <SortDesc size={16} />
                Sort Order
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setSortOrder("newest")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    sortOrder === "newest"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Newest First</div>
                    <div className="text-body-sm text-on-surface-variant">Show most recent emails at the top</div>
                  </div>
                  <CircleDot size={20} className={sortOrder === "newest" ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setSortOrder("oldest")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    sortOrder === "oldest"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Oldest First</div>
                    <div className="text-body-sm text-on-surface-variant">Show oldest emails at the top</div>
                  </div>
                  <CircleDot size={20} className={sortOrder === "oldest" ? "text-primary" : "text-transparent"} />
                </button>
              </div>
            </div>

            {/* Messages Per Account */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <Hash size={16} />
                Messages Per Account
              </label>
              <div className="grid grid-cols-4 gap-2">
                {MESSAGES_PER_ACCOUNT_OPTIONS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    onClick={() => setMessagesPerAccount(count)}
                    className={`p-3 rounded-lg border text-center transition ${
                      messagesPerAccount === count
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-outline hover:bg-on-surface-variant/8"
                    }`}
                  >
                    {count}
                  </button>
                ))}
              </div>
              <div className="text-body-sm text-on-surface-variant mt-2">
                Higher values use more memory. Current total: {messagesPerAccount * 2} messages (2 accounts)
              </div>
            </div>

            {/* Display Mode */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <Layers size={16} />
                Display Mode
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setDisplayMode("aggregated")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    displayMode === "aggregated"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Aggregated by Time</div>
                    <div className="text-body-sm text-on-surface-variant">Mix emails from all accounts, sorted by date</div>
                  </div>
                  <CircleDot size={20} className={displayMode === "aggregated" ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setDisplayMode("byAccount")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    displayMode === "byAccount"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Grouped by Account</div>
                    <div className="text-body-sm text-on-surface-variant">Show each account's emails separately</div>
                  </div>
                  <CircleDot size={20} className={displayMode === "byAccount" ? "text-primary" : "text-transparent"} />
                </button>
              </div>
            </div>

            {/* Attachment Routing */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <Paperclip size={16} />
                Attachment Handling
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setAttachmentRouting("fileTransfer")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    attachmentRouting === "fileTransfer"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">File Transfer Drawer</div>
                    <div className="text-body-sm text-on-surface-variant">Route attachments through P2P transfer (no size limits)</div>
                  </div>
                  <CircleDot size={20} className={attachmentRouting === "fileTransfer" ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setAttachmentRouting("inline")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    attachmentRouting === "inline"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Inline Attachments</div>
                    <div className="text-body-sm text-on-surface-variant">Traditional email attachments (subject to size limits)</div>
                  </div>
                  <CircleDot size={20} className={attachmentRouting === "inline" ? "text-primary" : "text-transparent"} />
                </button>
              </div>
            </div>

            {/* Load Images Toggle */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <Image size={16} />
                HTML Content &amp; Images
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setLoadImages(false)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    !loadImages
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Text Only (Secure)</div>
                    <div className="text-body-sm text-on-surface-variant">Show plain text, hide HTML and remote images</div>
                  </div>
                  <CircleDot size={20} className={!loadImages ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setLoadImages(true)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    loadImages
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Render HTML &amp; Images</div>
                    <div className="text-body-sm text-on-surface-variant">Display rich HTML content and load remote images</div>
                  </div>
                  <CircleDot size={20} className={loadImages ? "text-primary" : "text-transparent"} />
                </button>
              </div>
              <div className="text-body-sm text-on-surface-variant mt-2">
                ⚠️ Loading images may expose your IP address to senders
              </div>
            </div>

            {/* Whitelist/Blacklist (Placeholder) */}
            <div className="opacity-60">
              <label className="text-body-sm text-on-surface-variant mb-2 flex items-center gap-2">
                <Filter size={16} />
                Sender Filters (Coming Soon)
              </label>
              <div className="p-4 border border-outline rounded-lg bg-surface-container">
                <div className="text-body-sm text-on-surface-variant">
                  Whitelist and blacklist filters for email senders will be available in a future update.
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-6">
            <mui.Button
              onClick={() => setOpen(false)}
              variant="surface"
            >
              Cancel
            </mui.Button>
            <mui.Button
              onClick={handleSave}
              variant="primary"
              filled
            >
              Save
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}
